import React, { useEffect } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";
import defaultAvatar from "./studentava.jpg";

/* ============================================================
   ZION PRAISE ACADEMY — OFFICIAL DOCUMENT COLOR SYSTEM
============================================================ */

const COLORS = {
  navy: "#102A43",
  royalBlue: "#174EA6",
  blue: "#2563EB",
  skyBlue: "#EAF3FF",
  gold: "#D6A83F",
  goldLight: "#FFF8E7",

  ink: "#1F2937",
  charcoal: "#374151",
  gray: "#64748B",
  grayLight: "#94A3B8",
  border: "#D9E2EC",
  background: "#F4F7FB",
  white: "#FFFFFF",

  green: "#15803D",
  greenLight: "#ECFDF3",
  red: "#B91C1C",
  redLight: "#FEF2F2",
};

/* ============================================================
   A4 LANDSCAPE DOCUMENT STYLES
============================================================ */

const styles = StyleSheet.create({
  page: {
    backgroundColor: COLORS.background,
    paddingTop: 18,
    paddingBottom: 13,
    paddingHorizontal: 22,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.ink,
  },

  /* Header */

  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.navy,
    borderRadius: 7,
    paddingVertical: 10,
    paddingHorizontal: 13,
    borderBottomWidth: 3,
    borderBottomColor: COLORS.gold,
    marginBottom: 9,
  },

  schoolSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 48,
    height: 48,
    objectFit: "contain",
    backgroundColor: COLORS.white,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: COLORS.gold,
    marginRight: 11,
  },

  schoolDetails: {
    flex: 1,
  },

  schoolName: {
    fontSize: 15,
    fontWeight: "bold",
    color: COLORS.white,
    marginBottom: 3,
  },

  schoolMotto: {
    fontSize: 8,
    color: "#E5EDF8",
    marginBottom: 3,
  },

  schoolAddress: {
    fontSize: 7,
    color: "#CBD5E1",
  },

  secureBadge: {
    backgroundColor: "#1E426A",
    borderWidth: 1,
    borderColor: COLORS.gold,
    borderRadius: 5,
    paddingVertical: 7,
    paddingHorizontal: 11,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 10,
  },

  secureBadgeTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.white,
    letterSpacing: 1,
  },

  secureBadgeSubtitle: {
    fontSize: 6,
    color: "#E5EDF8",
    marginTop: 3,
  },

  /* Document title */

  titleBox: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.gold,
    borderRadius: 5,
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginBottom: 9,
  },

  title: {
    textAlign: "center",
    color: COLORS.navy,
    fontSize: 12,
    fontWeight: "bold",
    letterSpacing: 1,
  },

  subtitle: {
    textAlign: "center",
    color: COLORS.gray,
    fontSize: 7.5,
    marginTop: 3,
  },

  /* Main three-column area */

  mainContent: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: 8,
  },

  card: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    overflow: "hidden",
  },

  profileCard: {
    width: 157,
    alignItems: "center",
    paddingHorizontal: 10,
    paddingTop: 11,
    paddingBottom: 8,
    marginRight: 9,
  },

  profileAccent: {
    width: 42,
    height: 3,
    backgroundColor: COLORS.gold,
    borderRadius: 2,
    marginBottom: 8,
  },

  avatar: {
    width: 77,
    height: 77,
    objectFit: "cover",
    borderRadius: 39,
    borderWidth: 2,
    borderColor: COLORS.royalBlue,
    marginBottom: 8,
  },

  studentName: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    marginBottom: 5,
  },

  studentID: {
    fontSize: 8,
    color: COLORS.royalBlue,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 7,
  },

  verifiedBadge: {
    backgroundColor: COLORS.greenLight,
    borderWidth: 1,
    borderColor: "#BBF7D0",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 7,
    alignItems: "center",
  },

  verifiedText: {
    color: COLORS.green,
    fontSize: 7,
    fontWeight: "bold",
  },

  profileNote: {
    fontSize: 6.5,
    color: COLORS.gray,
    textAlign: "center",
    lineHeight: 1.35,
    marginTop: 8,
  },

  /* Section cards */

  infoCard: {
    flex: 1,
    marginRight: 9,
  },

  portalCard: {
    width: 226,
  },

  sectionHeader: {
    backgroundColor: COLORS.navy,
    paddingVertical: 7,
    paddingHorizontal: 9,
    flexDirection: "row",
    alignItems: "center",
  },

  sectionHeaderAccent: {
    width: 3,
    height: 11,
    backgroundColor: COLORS.gold,
    marginRight: 7,
    borderRadius: 1,
  },

  sectionHeaderText: {
    color: COLORS.white,
    fontWeight: "bold",
    fontSize: 8,
    letterSpacing: 0.4,
  },

  sectionBody: {
    paddingVertical: 6,
    paddingHorizontal: 9,
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: "#E8EDF3",
  },

  infoLabel: {
    width: 92,
    fontSize: 7.5,
    color: COLORS.gray,
  },

  infoValue: {
    flex: 1,
    fontSize: 7.5,
    color: COLORS.ink,
    fontWeight: "bold",
  },

  /* Portal information */

  portalIntro: {
    fontSize: 7,
    color: COLORS.gray,
    lineHeight: 1.4,
    marginBottom: 5,
  },

  credentialLabel: {
    fontSize: 7,
    color: COLORS.gray,
    marginBottom: 3,
  },

  credentialValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.royalBlue,
  },

  passwordValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.red,
  },

  credentialRow: {
    paddingVertical: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: "#E8EDF3",
  },

  accountStatus: {
    color: COLORS.green,
    fontSize: 8,
    fontWeight: "bold",
  },

  credentialWarning: {
    backgroundColor: COLORS.goldLight,
    borderWidth: 0.7,
    borderColor: "#E9D49A",
    borderRadius: 4,
    padding: 6,
    marginTop: 7,
  },

  credentialWarningTitle: {
    fontSize: 7,
    color: COLORS.navy,
    fontWeight: "bold",
    marginBottom: 4,
  },

  warningLine: {
    fontSize: 6.5,
    color: COLORS.charcoal,
    lineHeight: 1.4,
    marginBottom: 2,
  },

  /* Lower information row */

  bottomContent: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: 7,
  },

  securityCard: {
    flex: 1,
    marginRight: 9,
  },

  securityBody: {
    paddingVertical: 7,
    paddingHorizontal: 9,
  },

  securityLine: {
    fontSize: 7,
    color: COLORS.charcoal,
    lineHeight: 1.4,
    marginBottom: 3,
  },

  securityEmphasis: {
    color: COLORS.royalBlue,
    fontWeight: "bold",
  },

  verificationCard: {
    width: 295,
  },

  verificationBody: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
  },

  stampBox: {
    width: 73,
    height: 57,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.grayLight,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 7,
  },

  qrBox: {
    width: 73,
    height: 57,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: COLORS.grayLight,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 9,
  },

  placeholderTitle: {
    fontSize: 7,
    color: COLORS.navy,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 1.5,
  },

  placeholderSubtitle: {
    fontSize: 6,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 3,
  },

  signatureArea: {
    flex: 1,
    justifyContent: "flex-end",
    paddingBottom: 2,
  },

  signatureTitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 16,
  },

  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray,
    marginBottom: 4,
  },

  signatureCaption: {
    fontSize: 6.5,
    color: COLORS.gray,
  },

  /* Footer */

  footer: {
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 6,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  footerText: {
    fontSize: 6.5,
    color: COLORS.gray,
  },

  footerBrand: {
    fontSize: 6.5,
    color: COLORS.royalBlue,
    fontWeight: "bold",
  },
});

/* ============================================================
   REUSABLE SECTION HEADER
============================================================ */

const SectionHeader = ({ title }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionHeaderAccent} />
    <Text style={styles.sectionHeaderText}>{title}</Text>
  </View>
);

/* ============================================================
   STUDENT AUTHENTICATION PDF DOCUMENT
============================================================ */

const StudentAuthenticationPDF = ({ student }) => {
  const profileImage =
    student?.profile_image ||
    student?.student_admission_image ||
    defaultAvatar;

  const studentInformation = [
    ["Full Name", student?.full_name],
    ["Student ID", student?.admission_id],
    ["Gender", student?.gender],
    ["Class", student?.grade_class_name],
    ["Academic Year", student?.current_academic_year],
    ["Date of Birth", student?.date_of_birth],
    ["Guardian", student?.guardian_name],
    ["Guardian Phone", student?.guardian_phone],
  ];

  const emailOrUsername =
    student?.email || student?.admission_id || "N/A";

  const initialPassword =
    student?.initial_password || student?.password || "N/A";

  return (
    <Document
      title={`Student Authentication - ${student?.full_name || "Student"}`}
      author="Zion Praise Academy - CRM School System"
      subject="Official Student Authentication Document"
      creator="Zion Praise Academy CRM School System"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap={false}
      >
        {/* SCHOOL HEADER */}

        <View style={styles.header}>
          <View style={styles.schoolSection}>
            <Image src={schoolLogo} style={styles.logo} />

            <View style={styles.schoolDetails}>
              <Text style={styles.schoolName}>
                Zion Praise Academy - CRM School System
              </Text>

              <Text style={styles.schoolMotto}>
                Learning Today • Leading Tomorrow
              </Text>

              <Text style={styles.schoolAddress}>
                Cowfield 1st Junction, Duportroad, Paynesville,
                Monrovia, Liberia
              </Text>

              <Text style={styles.schoolAddress}>
                Official Student Authentication & Secure Identity Verification
              </Text>
            </View>
          </View>

          <View style={styles.secureBadge}>
            <Text style={styles.secureBadgeTitle}>
              SECURE RECORD
            </Text>
            <Text style={styles.secureBadgeSubtitle}>
              STUDENT AUTHENTICATION
            </Text>
          </View>
        </View>

        {/* DOCUMENT TITLE */}

        <View style={styles.titleBox}>
          <Text style={styles.title}>
            OFFICIAL STUDENT AUTHENTICATION DOCUMENT
          </Text>

          <Text style={styles.subtitle}>
            This document contains student registration details and
            school portal access information for authorized use.
          </Text>
        </View>

        {/* THREE-COLUMN STUDENT INFORMATION */}

        <View style={styles.mainContent}>
          {/* STUDENT PROFILE */}

          <View style={[styles.card, styles.profileCard]}>
            <View style={styles.profileAccent} />

            <Image
              src={profileImage}
              style={styles.avatar}
            />

            <Text style={styles.studentName}>
              {student?.full_name || "N/A"}
            </Text>

            <Text style={styles.studentID}>
              ID: {student?.admission_id || "N/A"}
            </Text>

            <View style={styles.verifiedBadge}>
              <Text style={styles.verifiedText}>
                ✓ REGISTERED STUDENT
              </Text>
            </View>

            <Text style={styles.profileNote}>
              Student identity should be confirmed against
              official school records.
            </Text>
          </View>

          {/* STUDENT INFORMATION */}

          <View style={[styles.card, styles.infoCard]}>
            <SectionHeader title="STUDENT INFORMATION" />

            <View style={styles.sectionBody}>
              {studentInformation.map(([label, value], index) => (
                <View style={styles.infoRow} key={index}>
                  <Text style={styles.infoLabel}>{label}</Text>

                  <Text style={styles.infoValue}>
                    {value || "N/A"}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* STUDENT PORTAL ACCESS */}

          <View style={[styles.card, styles.portalCard]}>
            <SectionHeader title="STUDENT PORTAL ACCESS" />

            <View style={styles.sectionBody}>
              <Text style={styles.portalIntro}>
                Use the account details below to identify the
                student's portal login account.
              </Text>

              <View style={styles.credentialRow}>
                <Text style={styles.credentialLabel}>
                  Email / Username
                </Text>

                <Text style={styles.credentialValue}>
                  {emailOrUsername}
                </Text>
              </View>

              <View style={styles.credentialRow}>
                <Text style={styles.credentialLabel}>
                  Initial Password
                </Text>

                <Text style={styles.passwordValue}>
                  {initialPassword}
                </Text>
              </View>

              <View style={styles.credentialRow}>
                <Text style={styles.credentialLabel}>
                  Account Status
                </Text>

                <Text style={styles.accountStatus}>
                  ACTIVE
                </Text>
              </View>

              <View style={styles.credentialWarning}>
                <Text style={styles.credentialWarningTitle}>
                  IMPORTANT SECURITY GUIDELINES
                </Text>

                <Text style={styles.warningLine}>
                  • Keep account credentials confidential.
                </Text>

                <Text style={styles.warningLine}>
                  • Change the initial password after first login.
                </Text>

                <Text style={styles.warningLine}>
                  • Never share login details with unauthorized persons.
                </Text>

                <Text style={styles.warningLine}>
                  • Contact school administration if access is compromised.
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* SECURITY AND VERIFICATION */}

        <View style={styles.bottomContent}>
          {/* SECURITY NOTICE */}

          <View style={[styles.card, styles.securityCard]}>
            <SectionHeader title="SECURITY & AUTHENTICITY NOTICE" />

            <View style={styles.securityBody}>
              <Text style={styles.securityLine}>
                • This document is generated from the school's
                student management system.
              </Text>

              <Text style={styles.securityLine}>
                • Student information and photograph should match
                the school's official records.
              </Text>

              <Text style={styles.securityLine}>
                • Unauthorized alteration or reproduction may
                invalidate this document.
              </Text>

              <Text style={styles.securityLine}>
                • Verification should be conducted through the
                school administration office.
              </Text>

              <Text style={styles.securityLine}>
                <Text style={styles.securityEmphasis}>
                  CONFIDENTIAL:
                </Text>
                {" "}For the registered student and authorized
                school officials only.
              </Text>
            </View>
          </View>

          {/* STAMP, QR PLACEHOLDER AND SIGNATURE */}

          <View style={[styles.card, styles.verificationCard]}>
            <SectionHeader title="OFFICIAL VERIFICATION" />

            <View style={styles.verificationBody}>
              <View style={styles.stampBox}>
                <Text style={styles.placeholderTitle}>
                  SCHOOL{"\n"}OFFICIAL{"\n"}STAMP
                </Text>
              </View>

              <View style={styles.qrBox}>
                <Text style={styles.placeholderTitle}>
                  QR CODE
                </Text>

                <Text style={styles.placeholderSubtitle}>
                  Verification
                </Text>
              </View>

              <View style={styles.signatureArea}>
                <Text style={styles.signatureTitle}>
                  Authorized Signature
                </Text>

                <View style={styles.signatureLine} />

                <Text style={styles.signatureCaption}>
                  Principal / Administrator
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* DOCUMENT FOOTER */}

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © {new Date().getFullYear()} Zion Praise Academy
          </Text>

          <Text style={styles.footerBrand}>
            OFFICIAL STUDENT AUTHENTICATION RECORD - POWERED BY POLU SCHOOL MANAGEMENT SYSTEM
          </Text>

          <Text style={styles.footerText}>
          Check school Potal on Goggle or Chrome: Zion Praise Academy or www.zionpraiseacademy.com • Confidential School Record
          </Text>
        </View>
      </Page>
    </Document>
  );
};

/* ============================================================
   AUTOMATIC SECURE PDF DOWNLOAD
============================================================ */

const Secure = ({ student }) => {
  useEffect(() => {
    if (!student) return;

    let isMounted = true;
    let downloadUrl = null;

    const generatePDF = async () => {
      try {
        const blob = await pdf(
          <StudentAuthenticationPDF student={student} />
        ).toBlob();

        if (!isMounted) return;

        downloadUrl = URL.createObjectURL(blob);

        const safeStudentId = String(
          student?.admission_id ||
          student?.student_id ||
          "Student"
        ).replace(/[^a-zA-Z0-9_-]/g, "_");

        const fileName =
          `ZPA_Student_Authentication_${safeStudentId}.pdf`;

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("PDF Generation Error:", error);
      }
    };

    generatePDF();

    return () => {
      isMounted = false;

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [student]);

  return null;
};

export default Secure;
